<script setup lang="ts">
/**
 * SectionAccordion
 *
 * FAQ section with expandable accordion items.
 * Uses shadcn-vue Accordion for accessibility and consistent behavior.
 *
 * Interaction: "Expand this"
 * - Hover: Background tint to show touch target
 * - Open: Background tint + primary color text
 */
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import type { SectionAccordionProps } from "~~/shared/types";

const props = withDefaults(defineProps<SectionAccordionProps>(), {
  sectionBg: "white",
});

const bgClass = computed(() =>
  props.sectionBg === "neutral" ? "bg-neutral-50" : "bg-white"
);

</script>

<template>
  <section :class="[bgClass, 'py-16 sm:py-24']">
    <div class="mx-auto max-w-4xl px-8">
      <!-- Header -->
      <div class="mb-12 text-center">
        <h2
          class="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl text-balance"
        >
          {{ title }}
        </h2>
      </div>

      <!-- Accordion (ClientOnly to prevent hydration mismatch from radix-vue ID generation) -->
      <ClientOnly>
        <Accordion
          type="single"
          collapsible
          class="rounded-2xl border border-neutral-200 bg-white"
        >
          <AccordionItem
            v-for="(item, index) in items"
            :key="index"
            :value="`item-${index}`"
            class="border-b border-neutral-200 last:border-b-0"
          >
            <AccordionTrigger
              class="px-6 py-5 text-left text-lg font-semibold text-slate-900 transition-all hover:bg-neutral-50 hover:text-primary-600 hover:no-underline data-[state=open]:bg-neutral-50 data-[state=open]:text-primary-600 [&[data-state=open]>svg]:text-primary-400"
            >
              {{ item.question }}
            </AccordionTrigger>
            <AccordionContent class="px-6 pb-5 leading-relaxed text-neutral-600">
              {{ item.answer }}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <template #fallback>
          <div class="rounded-2xl border border-neutral-200 bg-white">
            <div v-for="(item, index) in items" :key="index" class="border-b border-neutral-200 last:border-b-0 px-6 py-5">
              <div class="flex items-center justify-between">
                <span class="text-lg font-semibold text-slate-900">{{ item.question }}</span>
                <Icon name="lucide:chevron-down" class="size-5 text-slate-400" />
              </div>
            </div>
          </div>
        </template>
      </ClientOnly>
    </div>
  </section>
</template>
